import PocketBase from 'pocketbase';

const BASE_URL = process.env.VUE_APP_PB_URL || '/pb';
export const pb = new PocketBase(BASE_URL);

// ── Auth ──────────────────────────────────────────────────────────────────────

export function isLoggedIn() {
  return pb.authStore.isValid;
}

export function currentUser() {
  return pb.authStore.record;
}

export async function loginUser(email, password) {
  return pb.collection('users').authWithPassword(email, password);
}

export async function logoutUser() {
  pb.authStore.clear();
}

export async function refreshAuth() {
  if (pb.authStore.isValid) {
    return pb.collection('users').authRefresh();
  }
}

export async function changePassword(oldPassword, newPassword) {
  const user = pb.authStore.record;
  return pb.collection('users').update(user.id, {
    oldPassword,
    password: newPassword,
    passwordConfirm: newPassword,
  });
}

// ── Transfers ─────────────────────────────────────────────────────────────────

export async function saveTransfer(data) {
  return pb.collection('transfers').create({
    ...data,
    user: pb.authStore.record?.id,
  });
}

export async function getTransfers({ page = 1, perPage = 100, filter = '', sort = '-created' } = {}) {
  return pb.collection('transfers').getList(page, perPage, { filter, sort });
}

// ── Payout Queues ─────────────────────────────────────────────────────────────

export async function saveQueue(data) {
  return pb.collection('payout_queues').create({
    ...data,
    user: pb.authStore.record?.id,
  });
}

export async function updateQueue(id, data) {
  return pb.collection('payout_queues').update(id, data);
}

export async function getQueues() {
  return pb.collection('payout_queues').getList(1, 100, { sort: '-created' });
}

export async function deleteQueue(id) {
  return pb.collection('payout_queues').delete(id);
}

// ── Payout Targets ────────────────────────────────────────────────────────────

export async function saveTargets(queueId, targets) {
  return Promise.all(
    targets.map((t, i) =>
      pb.collection('payout_targets').create({
        ...t,
        queue_id: queueId,
        order_index: i,
      })
    )
  );
}

export async function updateTarget(id, data) {
  return pb.collection('payout_targets').update(id, data);
}

export async function getTargets(queueId) {
  return pb.collection('payout_targets').getList(1, 200, {
    filter: `queue_id = "${queueId}"`,
    sort: 'order_index',
  });
}

export async function deleteTargetsByQueue(queueId) {
  const list = await getTargets(queueId);
  return Promise.all(list.items.map(t => pb.collection('payout_targets').delete(t.id)));
}

// ── AML Logs ──────────────────────────────────────────────────────────────────

export async function logAmlCheck(data) {
  try {
    return await pb.collection('aml_logs').create({
      ...data,
      user: pb.authStore.record?.id,
    });
  } catch (e) {
    // Don't let logging failures block the UI
    console.warn('AML log failed:', e.message);
  }
}

// ── Address Book ──────────────────────────────────────────────────────────────

export async function getAddresses() {
  return pb.collection('address_book').getList(1, 500, { sort: 'label,address' });
}

export async function saveAddress(data) {
  return pb.collection('address_book').create({
    ...data,
    user: pb.authStore.record?.id,
  });
}

export async function updateAddress(id, data) {
  return pb.collection('address_book').update(id, data);
}

export async function deleteAddress(id) {
  return pb.collection('address_book').delete(id);
}

// ── User Settings ─────────────────────────────────────────────────────────────

export async function loadSettings() {
  try {
    const uid = pb.authStore.record?.id;
    if (!uid) return null;
    return await pb.collection('user_settings').getFirstListItem(`user = "${uid}"`);
  } catch {
    return null;
  }
}

export async function saveSettings(data) {
  const uid = pb.authStore.record?.id;
  if (!uid) return;
  const existing = await loadSettings();
  if (existing) {
    return pb.collection('user_settings').update(existing.id, data);
  }
  return pb.collection('user_settings').create({ ...data, user: uid });
}
